// RegisterView.js
// Wella's interface on her iPhone 14.
// Used at the booth to issue Golden Tickets to customers after payment.
// Two ticket types: Flash (~$10-15) and Custom (~$30).
// Supports mixed-batch printing: build a Flash+Custom combo, print all in one connection.

import { useState, useEffect, useRef } from "react";
import { db, storage } from "./firebase";
import { collection, doc, setDoc, addDoc, updateDoc, getDocs, runTransaction, serverTimestamp, onSnapshot, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import QRCode from "qrcode";
import useColorPalette from "./hooks/useColorPalette";
import useFlashDesigns from "./hooks/useFlashDesigns";
import useCurrentShiftOrders from "./hooks/useCurrentShiftOrders";
import useActiveShift from "./hooks/useActiveShift";
import ShiftPicker, { ShiftChip } from "./components/ShiftPicker";
import FramingTool from "./FramingTool";
import { printJobs } from "./utils/printer";
import { computeQueueWait, fmtWait } from "./utils/overlayFormat";
import { unfulfilledAddOns } from "./utils/combos";
import { shortLabel } from "./utils/meditations";
import { STAFF_THEME, FONT } from "./theme/staff";

// ── CONSTANTS ──────────────────────────────────────────────────────────────
// BRAND is sourced from the shared staff theme; `orange` here stays as the
// warning-orange (not the brand orange) because RegisterView uses it for
// printer-napping alerts and queue-closed warnings.
const BRAND = { ...STAFF_THEME, orange: STAFF_THEME.warning };
const PRINTER_IP   = "192.168.1.69";
const PRINTER_PORT = "80";
// The printer's built-in web config page (EpsonNet). Tapping "Open Printer Page"
// loads this — staff can power-cycle / reconnect from here without crawling under
// the table. Booth Wi-Fi is the Netgear Nighthawk hotspot.
const PRINTER_CONFIG_URL = `http://${PRINTER_IP}`;
const BOOTH_WIFI_NAME    = "the Nighthawk booth hotspot";

// True when an error is about the printer not responding (vs. a queue/email/etc
// error) — gates the dummy-simple troubleshooting card so it only shows when relevant.
function isPrinterError(msg = "") {
  return /napping|printer|network|didn't respond/i.test(msg);
}

// ── HELPERS ────────────────────────────────────────────────────────────────
function generateTokenId() { return crypto.randomUUID(); }

const EXPIRY_OPTIONS = [
  { value: 20,      label: "20 min"  },
  { value: 30,      label: "30 min"  },
  { value: 60,      label: "1 hr"    },
  { value: 120,     label: "2 hrs"   },
  { value: 240,     label: "4 hrs"   },
  { value: 1440,    label: "1 day"   },
  { value: 10080,   label: "1 week"  },
  { value: 43200,   label: "1 month" },
  { value: 525960,  label: "1 year"  },
  { value: 0,       label: "Never"   },
];

// Returns null for "never" (0); Firestore null skips expiry check in validateToken.
function computeExpiry(minutes) {
  if (minutes === 0) return null;
  return new Date(Date.now() + minutes * 60 * 1000);
}

// Human-readable expiry string printed on the ticket
function formatExpiryDisplay(minutes) {
  if (minutes === 0)     return "NO EXPIRY";
  if (minutes < 60)      return `VALID FOR ${minutes} MIN`;
  if (minutes < 1440)    return `VALID FOR ${minutes / 60} HR${minutes > 60 ? "S" : ""}`;
  if (minutes === 1440)  return "VALID FOR 1 DAY";
  if (minutes === 10080) return "VALID FOR 1 WEEK";
  if (minutes === 43200) return "VALID FOR 1 MONTH";
  return "VALID FOR 1 YEAR";
}

// ── LIVE QUEUE STATUS STRIP ────────────────────────────────────────────────
// Mirrors the overlay TopBar numbers (same hook + computeQueueWait) so register
// staff can quote queue depth / current pancake / wait without leaving the view.
function LiveQueueStatus({ activeShift, drawing, cookingAll, queued, allActive }) {
  if (!activeShift) return null;
  const queueDepth = [drawing, ...(cookingAll || []), ...(queued || [])].filter(Boolean).length;
  const closed     = activeShift.isQueueOpen === false;
  const working    = drawing || (cookingAll || [])[0] || null;

  const cellStyle  = { flex: 1, minWidth: 0, textAlign: "center" };
  const labelStyle = { color: BRAND.muted, fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", whiteSpace: "nowrap" };
  const valueStyle = { fontFamily: FONT.display, fontWeight: 700, fontSize: "1rem", marginTop: "0.1rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

  return (
    <div style={{ width: "100%", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, padding: "0.45rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem", boxSizing: "border-box" }}>
      <div style={{ ...cellStyle, flex: "0 0 auto" }}>
        <div style={labelStyle}>In queue</div>
        <div style={{ ...valueStyle, color: BRAND.gold }}>{queueDepth}</div>
      </div>
      <div style={cellStyle}>
        <div style={labelStyle}>{drawing ? "Drawing now" : working ? "On griddle" : "Drawing now"}</div>
        <div style={{ ...valueStyle, color: "white" }}>{working ? shortLabel(working) : "—"}</div>
      </div>
      <div style={{ ...cellStyle, flex: "0 0 auto" }}>
        <div style={labelStyle}>Est wait</div>
        <div style={{ ...valueStyle, color: closed ? BRAND.orange : BRAND.sky }}>
          {closed ? "Closed" : fmtWait(computeQueueWait(allActive || [], activeShift))}
        </div>
      </div>
      {/* Combo goods still owed across the shift — register staffer is often
          the one handing kits/prints over, so the count rides the status bar.
          Hidden at zero. */}
      {(() => {
        const owed = (allActive || []).reduce((n, o) => n + unfulfilledAddOns(o).length, 0);
        if (!owed) return null;
        return (
          <div style={{ ...cellStyle, flex: "0 0 auto" }}>
            <div style={labelStyle}>🎁 Owed</div>
            <div style={{ ...valueStyle, color: BRAND.gold }}>{owed}</div>
          </div>
        );
      })()}
    </div>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────
// `classic` renders the pre-2026-09-25 register unchanged, still reachable at
// /register-classic. The thermal-ticket era isn't deleted — Dan may want booth
// printing back when a wider food-popup menu lands, and the blocks below are
// the only record of how it worked. Everything gated on `classic` is archived,
// not dead: flip the route and it all comes back exactly as it was.
function RegisterView({ user, navigate, classic = false }) {
  const COLOR_OPTIONS = useColorPalette();
  const [issuing, setIssuing]         = useState(false);
  const [lastBatch, setLastBatch]     = useState(null);
  // 📱 Screen-QR flow — issued tickets shown full-screen for the guest to
  // scan off the iPad (comps, cash payers, no printer on the road).
  const [showScreenQR, setShowScreenQR]   = useState(false);
  const [screenQRIndex, setScreenQRIndex] = useState(0);
  const [error, setError]             = useState(null);
  const [printStatus, setPrintStatus] = useState({ pct: 0, msg: "" });
  const printerFailCount              = useRef(0);
  const [showPrinterHelp, setShowPrinterHelp] = useState(false);
  const [flashQty, setFlashQty]       = useState(0);
  const [customQty, setCustomQty]     = useState(0);
  const [magicQty, setMagicQty]       = useState(0);
  const [customerEmail, setCustomerEmail] = useState("");
  const [activeShift, setActiveShift]             = useState(null);
  const { allDesigns } = useFlashDesigns(activeShift);
  // Live queue stats for the status strip — same hook the overlays use, so the
  // numbers Wella quotes at the register always match the screen.
  const { drawing, cookingAll, queued, allActive } = useCurrentShiftOrders();
  const [shiftChecked, setShiftChecked]           = useState(false);
  const [showQueueClosedWarning, setShowQueueClosedWarning] = useState(false);
  const [rejectedAlert, setRejectedAlert]         = useState(false);
  const dismissedRejectionIds = useRef(new Set(
    JSON.parse(localStorage.getItem("gt_dismissed_rejections") || "[]")
  ));

  // ── DIRECT QUEUE ENTRY ────────────────────────────────────────────────────
  const [showDirectEntry, setShowDirectEntry]     = useState(false);
  // What was just added, for the success screen: the number Wella reads out
  // and the order to hang a keepsake email on.
  const [lastAdded, setLastAdded]                 = useState(null);
  const [successEmail, setSuccessEmail]           = useState("");
  const [emailState, setEmailState]               = useState("idle"); // idle | saving | saved | error
  // Secret Ticket state removed — /campaigns workspace owns that flow.
  const [directType, setDirectType]               = useState(null);   // null = nothing selected yet; "flash" or "custom" once user picks
  const [directName, setDirectName]               = useState("");
  const [directSubjectName, setDirectSubjectName] = useState("");
  const [directNotes, setDirectNotes]             = useState("");
  const [directIsComp, setDirectIsComp]           = useState(false);
  const [directCompReason, setDirectCompReason]   = useState("");
  const [directImage, setDirectImage]             = useState(null);
  const [directColors, setDirectColors]           = useState([]);
  const [directDesign, setDirectDesign]           = useState(null);  // { id, label, file }
  const [directFreestyleDesc, setDirectFreestyleDesc] = useState("");
  const [directCroppedBlob, setDirectCroppedBlob] = useState(null);
  const directFramingRef                          = useRef({});  // { drawnWidthFrac, drawnHeightFrac, offsetXFrac, offsetYFrac }
  const [directSubmitting, setDirectSubmitting]   = useState(false);
  const [directError, setDirectError]             = useState(null);
  // Defaults ON for the classic (thermal) register, where a verbal order still
  // wanted a paper ticket. The printerless register must never inherit it —
  // the toggle isn't rendered there, so the flag would be invisible AND true,
  // and every order would reach for a printer that isn't on the network.
  // `wantsPrint` below is the only value anything may act on.
  const [directPrintRequested, setDirectPrintRequested] = useState(true);

  // ── EXPIRY SETTINGS — persisted to localStorage ───────────────────────────
  const [expiryMinutes, setExpiryMinutesRaw] = useState(() => parseInt(localStorage.getItem("gt_expiry_minutes")) || 20);

  function setExpiryMinutes(v)  { setExpiryMinutesRaw(v);  localStorage.setItem("gt_expiry_minutes", String(v)); }

  // ── SIGNATURE LINE — toggle persisted to localStorage ─────────────────────
  const [showSignature, setShowSignatureRaw] = useState(() => localStorage.getItem("gt_show_signature") === "true");

  function setShowSignature(v) { setShowSignatureRaw(v); localStorage.setItem("gt_show_signature", String(v)); }

  const totalQty = flashQty + customQty + magicQty;

  const expiryPreview = formatExpiryDisplay(expiryMinutes);

  useEffect(() => {
    fetch(`http://${PRINTER_IP}`, { mode: "no-cors" }).catch(() => {});
  }, []);

  // Which queue is this register working? (2026-09-25) Was an isActive →
  // docs[0] query, which silently issued tickets into whichever concurrent
  // event Firestore happened to return first. The register is the highest-
  // stakes surface for this — a misrouted ticket is a guest standing at the
  // wrong booth holding a number that means nothing there.
  const {
    shift: scopedShift,
    loading: scopeLoading,
    candidates: activeCandidates,
    needsChoice: needsShiftChoice,
    isConcurrent,
    bind: bindShift,
  } = useActiveShift();
  const [showShiftPicker, setShowShiftPicker] = useState(false);

  useEffect(() => {
    setActiveShift(scopedShift);
    setShiftChecked(!scopeLoading);
  }, [scopedShift, scopeLoading]);

  // Watch for rejected orders in the current shift — alert Wella to intercept the customer
  useEffect(() => {
    if (!activeShift) return;
    const q = query(
      collection(db, "orders"),
      where("shiftId", "==", activeShift.id),
      where("state", "==", "rejected")
    );
    return onSnapshot(q, snap => {
      const newIds = snap.docs
        .filter(d => !d.data().trashedAt)
        .map(d => d.id)
        .filter(id => !dismissedRejectionIds.current.has(id));
      if (newIds.length > 0) setRejectedAlert(newIds);
    });
  }, [activeShift?.id]);

  function resetDirectEntry() {
    setDirectType(null);
    setDirectPrintRequested(true);
    setDirectName("");
    setDirectSubjectName("");
    setDirectNotes("");
    setDirectIsComp(false);
    setDirectCompReason("");
    setDirectImage(null);
    setDirectColors([]);
    setDirectDesign(null);
    setDirectFreestyleDesc("");
    setDirectCroppedBlob(null);
    directFramingRef.current = {};
    setDirectError(null);
  }

  // Printing is a classic-only capability. One derived flag so the label and
  // the write path can't disagree.
  const wantsPrint = classic && directPrintRequested;

  const handleDirectEntry = async () => {
    if (!activeShift || directSubmitting) return;
    if (directType === "flash" && !directDesign) { setDirectError("Select a design."); return; }
    setDirectSubmitting(true);
    setDirectError(null);
    try {
      // Atomically assign queue number from the CANONICAL shift counter
      // (shifts/{id}/meta/<doc>.lastNumber) — the same counter every other
      // order path uses (FlashOrderView/CustomerOrderView/ArtistQueueView via
      // assignQueueNumber). Previously this read shift.lastQueueNumber, a
      // field nothing seeds or reads, so every staff order restarted at #1 and
      // collided with the live queue.
      const metaSnap = await getDocs(collection(db, "shifts", activeShift.id, "meta"));
      if (metaSnap.empty) {
        setDirectError("Shift counter not found — close and reopen the queue.");
        setDirectSubmitting(false);
        return;
      }
      const counterRef = metaSnap.docs[0].ref;
      let queueNumber;
      await runTransaction(db, async (tx) => {
        const counterDoc = await tx.get(counterRef);
        const current = counterDoc.data().lastNumber ?? 0;
        queueNumber = current + 1;
        tx.update(counterRef, { lastNumber: queueNumber });
      });

      // Mint a token immediately so the order is reissuable later via the
      // standard ReissueTicketModal flow (which gates on order.tokenId).
      // status="used" because the staff entered the order verbally — no
      // customer scan event to record. issuedBy stamped for audit.
      const tokenId = crypto.randomUUID();
      await setDoc(doc(collection(db, "tokens"), tokenId), {
        id:         tokenId,
        createdAt:  serverTimestamp(),
        usedAt:     serverTimestamp(),
        expiresAt:  null,
        status:     "used",
        ticketType: directType,
        source:     "staff",
        ...(activeShift._isTestData ? { _isTestData: true } : {}),
        issuedBy:   user?.email || "register",
        shiftId:    activeShift.id,
        squareOrderId: null,
        isComp:     directIsComp,
        compReason: directIsComp ? (directCompReason.trim() || null) : null,
      });

      // Upload original image and cropped blob if custom + image provided
      let imageUrl = null;
      let croppedImageUrl = null;
      if (directType === "custom" && directImage) {
        const fileId = crypto.randomUUID();
        // Path must be orders/{id}/{file} (2 segments after `orders/`) to match
        // storage.rules `match /orders/{tokenId}/{filename}`. The old extra
        // `staff/` segment matched NO rule and was denied for everyone — incl.
        // staff — so register custom-photo uploads silently failed (imageUrl=null
        // → pancake reference "goes missing"). Keep consistent with every other
        // order-image upload path.
        const origRef = ref(storage, `orders/${fileId}/image.jpg`);
        await uploadBytes(origRef, directImage);
        imageUrl = await getDownloadURL(origRef);
        if (directCroppedBlob) {
          const cropRef = ref(storage, `orders/${fileId}/cropped.jpg`);
          await uploadBytes(cropRef, directCroppedBlob);
          croppedImageUrl = await getDownloadURL(cropRef);
        }
      }

      const isFreestyle = directDesign?.id === "freestyle";
      const fp = directFramingRef.current;

      // orderType drives wait-time estimates, the ⚡ Flash vs 🎨 Custom queue
      // chip, reference-image rendering, and overlay labels. ticketType alone
      // left staff flash orders rendering everywhere as "Custom".
      const orderType =
        directType === "flash"
          ? (isFreestyle ? "freestyle_flash" : "flash")
          : "custom";

      // Per-design lifetime sequence ("Saturnina (#6)" auto-naming). Only
      // flash/freestyle get one — custom one-offs don't, matching the customer
      // flow. Key is "freestyle" for freestyle, else the design id.
      let designSequence = null;
      if (directType === "flash") {
        const seqKey = isFreestyle ? "freestyle" : directDesign.id;
        const dcRef = doc(db, "config", "designCounters");
        designSequence = await runTransaction(db, async (tx) => {
          const snap = await tx.get(dcRef);
          const data = snap.exists() ? snap.data() : {};
          const next = (data[seqKey] || 0) + 1;
          tx.set(dcRef, { [seqKey]: next }, { merge: true });
          return next;
        }).catch(() => null);
      }

      const orderRef = await addDoc(collection(db, "orders"), {
        tokenId,
        shiftId:               activeShift.id,
        // Inherit the test flag — keeps Lab-driven register orders sealed.
        ...(activeShift._isTestData ? { _isTestData: true } : {}),
        queueNumber,
        ticketType:            directType,
        orderType,
        designSequence,
        recipientName:         directName.trim(),
        subjectName:           directSubjectName.trim() || null,
        notes:                 directNotes.trim() || (isFreestyle ? directFreestyleDesc.trim() : null) || null,
        isComp:                directIsComp,
        compReason:            directIsComp ? (directCompReason.trim() || null) : null,
        designId:              directDesign?.id ?? null,
        designLabel:           directDesign?.label ?? null,
        isFreestyle:           isFreestyle || false,
        favoriteColors:        directColors.length > 0 ? directColors : null,
        imageUrl,
        croppedImageUrl,
        framingDrawnWidthFrac:  fp.drawnWidthFrac  ?? null,
        framingDrawnHeightFrac: fp.drawnHeightFrac ?? null,
        framingOffsetXFrac:     fp.offsetXFrac     ?? null,
        framingOffsetYFrac:     fp.offsetYFrac     ?? null,
        state:            "queued",
        submittedAt:      serverTimestamp(),
        isStaffEntry:     true,
        source:           "staff",
        createdAt:        serverTimestamp(),
        artistName:       null,
        ai_description:   null,
        ai_tags:          null,
        photo_cutout_url: null,
      });

      // Backfill orderId onto the token so the keepsake page (/order?t=...)
      // can resolve to the order doc.
      await updateDoc(doc(db, "tokens", tokenId), { orderId: orderRef.id }).catch(() => {});

      // Optional: print a thermal ticket for the customer. Same template
      // as batch-issued tickets; uses the existing printJobs helper.
      if (wantsPrint) {
        try {
          const orderUrl = `${window.location.origin}/order?t=${tokenId}`;
          const qrDataUrl = await QRCode.toDataURL(orderUrl, {
            width: 300, margin: 2,
            color: { dark: "#000000", light: "#FFFFE0" },
          });
          await printJobs([{
            ticketType:    directType,
            tokenId,
            orderUrl,
            qrDataUrl,
            expiresAt:     null,
            expiryDisplay: "Issued at the booth",
            showSignature: false,
          }], {
            onStatus: () => {},
            printerIp: PRINTER_IP,
            printerPort: PRINTER_PORT,
          });
        } catch (printErr) {
          // Print failure shouldn't undo the order — it's already in the
          // queue. Surface the error so staff knows to retry print manually.
          console.warn("Direct-entry print failed:", printErr);
          setDirectError(`Order added (#${queueNumber}) but the printer didn't respond: ${printErr.message || "check the network"}. Reissue from the queue if needed.`);
          return; // bail BEFORE reset/close so staff sees the message
        }
      }

      // The success screen is the whole point of the redesign: the number is
      // what Wella reads to the guest, and the keepsake email is captured
      // HERE rather than before the order exists.
      setLastAdded({
        orderId:      orderRef.id,
        queueNumber,
        name:         directName.trim(),
        label:        directDesign?.label
                        ?? (directType === "custom" ? "Custom photo" : "Pancake"),
        isFreestyle:  directDesign?.id === "freestyle",
        freestyle:    directFreestyleDesc.trim() || null,
      });
      setSuccessEmail("");
      setEmailState("idle");
      resetDirectEntry();
      setShowDirectEntry(false);
    } catch (err) {
      console.error("Direct entry failed:", err);
      setDirectError(err.message);
    } finally {
      setDirectSubmitting(false);
    }
  };

  // customer_email is the field autoKeepsakeEmail watches — writing it here
  // means the guest's keepsake sends itself the moment the photo lands. No new
  // pipeline, no second address book.
  async function saveKeepsakeEmail() {
    const addr = successEmail.trim();
    if (!addr || !lastAdded?.orderId) return;
    setEmailState("saving");
    try {
      await updateDoc(doc(db, "orders", lastAdded.orderId), { customer_email: addr });
      setEmailState("saved");
    } catch (err) {
      console.error("Keepsake email save failed:", err);
      setEmailState("error");
    }
  }

  const issueBatch = async (screenOnly = false) => {
    if (issuing || totalQty === 0) return;
    setIssuing(true);
    setError(null);
    setPrintStatus({ pct: 0, msg: "" });

    // Snapshot expiry settings at batch start — consistent across all tickets in the batch
    const snapMinutes = expiryMinutes;
    const expiryDisplay = formatExpiryDisplay(snapMinutes);

    const jobTypes = [
      ...Array(flashQty).fill("flash"),
      ...Array(customQty).fill("custom"),
      ...Array(magicQty).fill("magic"),
    ];

    try {
      setPrintStatus({ pct: 10, msg: `Registering ${totalQty} ticket${totalQty > 1 ? "s" : ""}...` });

      const jobs = await Promise.all(jobTypes.map(async (ticketType) => {
        const tokenId   = generateTokenId();
        const expiresAt = computeExpiry(snapMinutes);
        const orderUrl  = `${window.location.origin}/order?t=${tokenId}`;

        const [, qrDataUrl] = await Promise.all([
          setDoc(doc(collection(db, "tokens"), tokenId), {
            id:            tokenId,
            createdAt:     serverTimestamp(),
            expiresAt:     expiresAt,
            usedAt:        null,
            status:        "issued",
            ticketType:    ticketType,
            source:        "booth",
            ...(activeShift?._isTestData ? { _isTestData: true } : {}),
            squareOrderId: null,
            isComp:        false,
            compReason:    null,
            issuedBy:      "register",
          }),
          QRCode.toDataURL(orderUrl, {
            width: 300, margin: 2,
            color: { dark: "#000000", light: "#FFFFE0" },
          }),
        ]);

        return { ticketType, tokenId, expiresAt, orderUrl, qrDataUrl, expiryDisplay, showSignature };
      }));

      const emailTarget = customerEmail.trim();
      if (screenOnly) {
        // ── Screen flow: no printer, no email — spin the iPad around and let
        // the guest scan straight off the display. Built for solo road events
        // (comps, cash payers, printer left at home).
        setPrintStatus({ pct: 100, msg: "📱 Ready — show this screen to the guest." });
        setShowScreenQR(true);
        setScreenQRIndex(0);
      } else if (emailTarget) {
        // ── Email flow: Cloud Function generates QR PNGs and sends via Brevo ──
        setPrintStatus({ pct: 45, msg: "Generating QR codes..." });
        const sendTicketEmail = httpsCallable(getFunctions(getApp()), "sendTicketEmail");
        const result = await sendTicketEmail({
          email: emailTarget,
          jobs:  jobs.map(j => ({
            ticketType:    j.ticketType,
            tokenId:       j.tokenId,
            orderUrl:      j.orderUrl,
            expiryDisplay: j.expiryDisplay,
          })),
        });
        console.log("sendTicketEmail result:", result);
        setPrintStatus({ pct: 100, msg: `📧 Sent! ${jobs.length > 1 ? `${jobs.length} tickets` : "Ticket"} on their way.` });
        setCustomerEmail("");
      } else {
        // ── Print flow (unchanged) ─────────────────────────────────────────────
        setPrintStatus({ pct: 40, msg: "Waking up the printer..." });
        await printBatch(jobs);
      }

      setLastBatch(jobs);
      setFlashQty(0);
      setCustomQty(0);
      setMagicQty(0);
      printerFailCount.current = 0;

    } catch (err) {
      console.error("Failed to issue batch:", err);
      if (err.message?.includes("napping")) {
        printerFailCount.current += 1;
        setError(printerFailCount.current >= 2
          ? "Printer's napping? Ensure you're on the printer's network! 📶"
          : err.message
        );
      } else {
        setError(err.message);
      }
      // Pop the dummy-simple troubleshooting card automatically on any printer error.
      if (isPrinterError(err.message)) setShowPrinterHelp(true);
    } finally {
      setIssuing(false);
    }
  };

  // Gate for print-only flow: warn if queue is closed on an active shift
  const handlePrintClick = () => {
    if (issuing || totalQty === 0) return;
    const isEmailFlow = customerEmail.trim().length > 0;
    if (!isEmailFlow && activeShift && activeShift.isQueueOpen === false) {
      setShowQueueClosedWarning(true);
      return;
    }
    issueBatch();
  };

  const printBatch = (jobs) => printJobs(jobs, {
    onStatus: setPrintStatus,
    printerIp: PRINTER_IP,
    printerPort: PRINTER_PORT,
  });

  // ── QTY COUNTER ──────────────────────────────────────────────────────────
  function QtyCounter({ label, emoji, qty, setQty, accentColor, textColor }) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", maxWidth: "320px" }}>
        <div style={{ flex: 1, background: BRAND.card, borderRadius: "12px", padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "1.3rem" }}>{emoji}</span>
          <span style={{ color: accentColor, fontWeight: "bold", fontSize: "1.1rem", flex: 1 }}>{label}</span>
        </div>
        <button onClick={() => setQty(q => Math.max(0, q - 1))} disabled={issuing || qty === 0}
          style={{ width: "44px", height: "44px", background: qty > 0 ? BRAND.card : BRAND.panel, color: qty > 0 ? "rgba(255,255,255,0.7)" : BRAND.border, border: "none", borderRadius: "10px", fontSize: "1.4rem", fontWeight: "bold", cursor: (issuing || qty === 0) ? "not-allowed" : "pointer" }}
        >−</button>
        <span style={{ width: "32px", textAlign: "center", color: qty > 0 ? accentColor : "rgba(255,255,255,0.35)", fontSize: "1.3rem", fontWeight: "bold" }}>{qty}</span>
        <button onClick={() => setQty(q => q + 1)} disabled={issuing}
          style={{ width: "44px", height: "44px", background: accentColor, color: textColor, border: "none", borderRadius: "10px", fontSize: "1.4rem", fontWeight: "bold", cursor: issuing ? "not-allowed" : "pointer" }}
        >+</button>
      </div>
    );
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  const lastFlash  = lastBatch?.filter(j => j.ticketType === "flash").length ?? 0;
  const lastCustom = lastBatch?.filter(j => j.ticketType === "custom").length ?? 0;
  const lastMagic  = lastBatch?.filter(j => j.ticketType === "magic").length ?? 0;
  const lastQR     = lastBatch?.[lastBatch.length - 1]?.qrDataUrl ?? null;

  // Concurrent events, unresolved scope: block before any ticket can be
  // issued. Unreachable on a one-queue day.
  if (needsShiftChoice || showShiftPicker) {
    return (
      <ShiftPicker
        candidates={activeCandidates}
        audience="staff"
        title="Which booth is this register?"
        subtitle="Two queues are open. Tickets issued here will join the queue you pick."
        onPick={(s) => { bindShift(s); setActiveShift(s); setShowShiftPicker(false); }}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 0 2rem", gap: "1rem" }}>

      {/* Top bar — ← Menu pinned to top right for consistency across the app. */}
      <div style={{ width: "100%", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, padding: "0.6rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem", rowGap: "0.5rem", flexWrap: "wrap" }}>
        <h1 style={{ color: BRAND.gold, margin: 0, fontFamily: FONT.display, fontSize: "1.1rem", fontWeight: 700 }}>🎟 Register</h1>
        <span style={{ color: BRAND.muted, fontSize: "0.78rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</span>
        {/* Which booth this register is feeding — only while concurrent
            queues exist, so a solo market's header is unchanged. */}
        <ShiftChip shift={activeShift} isConcurrent={isConcurrent} onChange={() => setShowShiftPicker(true)} />
        <button onClick={() => navigate("/menu")} style={{ background: "transparent", border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: BRAND.muted, fontSize: "0.78rem", fontWeight: 600, padding: "0.3rem 0.7rem", cursor: "pointer", flexShrink: 0 }}>
          ← Menu
        </button>
      </div>

      {/* Live queue status — flush under the top bar; negative margin eats the
          parent flex gap so the two read as one header unit. */}
      {activeShift && (
        <div style={{ width: "100%", marginTop: "-1rem" }}>
          <LiveQueueStatus
            activeShift={activeShift}
            drawing={drawing}
            cookingAll={cookingAll}
            queued={queued}
            allActive={allActive}
          />
        </div>
      )}

      {/* Content wrapper — centered below the top bar */}
      <div style={{ padding: "1.5rem 2rem 0", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem", width: "100%", boxSizing: "border-box" }}>

      {rejectedAlert && rejectedAlert.length > 0 && (
        <div style={{ width: "100%", maxWidth: "320px", background: "#3a0a0a", border: "1px solid #c0392b", borderRadius: "10px", padding: "0.85rem 1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ fontSize: "1.3rem" }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ color: "#e74c3c", fontWeight: "bold", fontSize: "0.9rem" }}>Order rejected</div>
            <div style={{ color: "#c0392b", fontSize: "0.78rem", marginTop: "0.15rem" }}>A customer's order couldn't be fulfilled — intercept them!</div>
          </div>
          <button onClick={() => {
            if (Array.isArray(rejectedAlert)) {
              rejectedAlert.forEach(id => dismissedRejectionIds.current.add(id));
              localStorage.setItem("gt_dismissed_rejections", JSON.stringify([...dismissedRejectionIds.current]));
            }
            setRejectedAlert(false);
          }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.35)", fontSize: "1.1rem", cursor: "pointer", padding: "0.25rem" }}>✕</button>
        </div>
      )}

      {shiftChecked && !activeShift && (
        <div style={{ width: "100%", maxWidth: "320px", background: "#1e1a0a", border: "1px solid #5a4a1a", borderRadius: "10px", padding: "0.75rem 1rem", textAlign: "center" }}>
          <span style={{ color: "#7a6a2a", fontSize: "0.85rem" }}>⏸ Queue not open — ask Dan to start the shift</span>
        </div>
      )}

      {/* ARCHIVED (thermal era): ticket batches, the email-instead-of-print
          field, and the print button. We stopped issuing paper tickets —
          guests binned the receipt and it fades. Orders are taken directly
          now; the keepsake email is captured AFTER the order instead. */}
      {classic && <>
      <QtyCounter label="FLASH"  emoji="⚡" qty={flashQty}  setQty={setFlashQty}  accentColor={BRAND.gold} textColor={BRAND.dark} />
      <QtyCounter label="CUSTOM" emoji="🎨" qty={customQty} setQty={setCustomQty} accentColor={BRAND.pink} textColor="white" />
      <QtyCounter label="MAGIC"  emoji="✨" qty={magicQty}  setQty={setMagicQty}  accentColor={BRAND.sky}  textColor="white" />

      <input
        type="email"
        value={customerEmail}
        onChange={e => setCustomerEmail(e.target.value)}
        placeholder="📧 Customer email — sends instead of printing"
        disabled={issuing}
        style={{
          width: "100%", maxWidth: "320px", boxSizing: "border-box",
          padding: "0.75rem 1rem",
          background: BRAND.card,
          border: `1px solid ${customerEmail.trim() ? BRAND.gold : BRAND.border}`,
          borderRadius: "10px",
          color: "white", fontSize: "0.9rem",
        }}
      />

      <button
        onClick={handlePrintClick}
        disabled={issuing || totalQty === 0}
        style={{
          width: "100%", maxWidth: "320px",
          background:   (issuing || totalQty === 0) ? BRAND.card : BRAND.gold,
          color:        (issuing || totalQty === 0) ? "rgba(255,255,255,0.35)" : BRAND.dark,
          border: "none", borderRadius: "14px", padding: "1.25rem",
          fontSize: "1.3rem", fontWeight: "bold",
          cursor: (issuing || totalQty === 0) ? "not-allowed" : "pointer",
          transition: "background 0.2s, color 0.2s", marginTop: "0.25rem",
        }}
      >
        {issuing
          ? (customerEmail.trim() ? "Sending..." : "Printing...")
          : totalQty === 0
            ? "Select tickets above"
            : customerEmail.trim()
              ? `📧 Email ${totalQty} Ticket${totalQty > 1 ? "s" : ""}`
              : `🖨 Print ${totalQty} Ticket${totalQty > 1 ? "s" : ""}`
        }
      </button>

      {/* 📱 Screen-QR issuance — no printer, no email. The iPad IS the
          ticket: issue, spin the screen around, guest scans with their
          camera and lands straight in the upload flow. */}
      {totalQty > 0 && !customerEmail.trim() && (
        <button
          onClick={() => issueBatch(true)}
          disabled={issuing}
          style={{
            width: "100%", maxWidth: "320px",
            background: "transparent", color: BRAND.sky,
            border: `1px solid ${BRAND.sky}`,
            borderRadius: "10px", padding: "0.85rem",
            fontSize: "0.95rem", fontWeight: "bold",
            cursor: issuing ? "not-allowed" : "pointer",
            opacity: issuing ? 0.5 : 1,
          }}
        >
          📱 Issue & Show QR on Screen
        </button>
      )}
      </>}

      {/* Add Order Directly — classic only; intake is the screen now. */}
      {classic && activeShift && (
        <button
          onClick={() => { setShowDirectEntry(true); setDirectError(null); }}
          disabled={issuing}
          style={{
            width: "100%", maxWidth: "320px",
            background: "transparent",
            color: "rgba(255,255,255,0.35)",
            border: "1px solid rgba(74,164,225,0.28)",
            borderRadius: "10px", padding: "0.75rem",
            fontSize: "0.85rem", fontWeight: "bold",
            cursor: issuing ? "not-allowed" : "pointer",
          }}
        >
          + Add Order Directly
        </button>
      )}

      {/* Campaigns is a marketing surface, not an intake one — it lives on
          the staff menu. Kept here in classic only. */}
      {classic && <button
        onClick={() => navigate("/campaigns")}
        disabled={issuing}
        style={{
          width: "100%", maxWidth: "320px",
          background: "transparent",
          color: BRAND.gold,
          border: `1px solid ${BRAND.gold}`,
          borderRadius: "10px", padding: "0.75rem",
          fontSize: "0.85rem", fontWeight: "bold",
          cursor: issuing ? "not-allowed" : "pointer",
          opacity: issuing ? 0.5 : 1,
        }}
      >
        🎟 Campaigns →
      </button>}

      {/* Progress bar — print/email batch progress, thermal era. */}
      {classic && issuing && printStatus.pct > 0 && (
        <div style={{ width: "100%", maxWidth: "320px" }}>
          <p style={{ color: "rgba(255,255,255,0.55)", margin: "0 0 0.5rem", fontSize: "0.85rem", textAlign: "center" }}>{printStatus.msg}</p>
          <div style={{ width: "100%", height: "6px", background: BRAND.card, borderRadius: "3px", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${printStatus.pct}%`, background: BRAND.gold, borderRadius: "3px", transition: "width 0.4s ease" }} />
          </div>
        </div>
      )}

      {classic && !issuing && printStatus.pct === 100 && (
        <p style={{ color: BRAND.gold, margin: 0, fontSize: "0.95rem", textAlign: "center" }}>{printStatus.msg}</p>
      )}

      {error && (
        <p style={{ color: BRAND.orange, margin: 0, textAlign: "center", fontSize: "0.9rem" }}>{error}</p>
      )}

      {/* ── PRINTER TROUBLESHOOTING ────────────────────────────────────────────
          Dummy-simple help for Rosie & Wella. Auto-pops on a printer error;
          also reachable any time via the "🖨 Printer trouble?" button below. */}
      {classic && !showPrinterHelp && (
        <button
          onClick={() => setShowPrinterHelp(true)}
          style={{
            background: "transparent", border: "none",
            color: "rgba(255,255,255,0.4)", fontSize: "0.8rem",
            cursor: "pointer", textDecoration: "underline", padding: "0.25rem",
          }}
        >
          🖨 Printer trouble?
        </button>
      )}

      {showPrinterHelp && (
        <div style={{
          width: "100%", maxWidth: "320px", boxSizing: "border-box",
          background: BRAND.card, border: `1px solid ${BRAND.gold}`,
          borderRadius: "14px", padding: "1rem 1.1rem",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <span style={{ color: BRAND.gold, fontWeight: "bold", fontSize: "1rem" }}>🖨 Printer not printing?</span>
            <button
              onClick={() => setShowPrinterHelp(false)}
              style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: "1.3rem", cursor: "pointer", lineHeight: 1, padding: 0 }}
              aria-label="Close printer help"
            >×</button>
          </div>

          <ol style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 0.85rem", paddingLeft: "1.2rem" }}>
            <li>Make sure this device is on <b>{BOOTH_WIFI_NAME}</b> Wi-Fi (not cell data or another network).</li>
            <li>Check the printer is <b>powered on</b> and has yellow paper loaded.</li>
            <li>Tap <b>Open Printer Page</b> below — if it loads, the printer is online and you can just print again. If it spins forever, the Wi-Fi is the problem (back to step 1).</li>
          </ol>

          <button
            onClick={() => window.open(PRINTER_CONFIG_URL, "_blank")}
            style={{
              width: "100%", background: BRAND.gold, color: BRAND.dark,
              border: "none", borderRadius: "10px", padding: "0.85rem",
              fontSize: "1rem", fontWeight: "bold", cursor: "pointer", marginBottom: "0.5rem",
            }}
          >
            🖨 Open Printer Page
          </button>

          {totalQty > 0 && (
            <button
              onClick={() => { setError(null); setShowPrinterHelp(false); handlePrintClick(); }}
              disabled={issuing}
              style={{
                width: "100%", background: "transparent", color: BRAND.gold,
                border: `1px solid ${BRAND.gold}`, borderRadius: "10px", padding: "0.75rem",
                fontSize: "0.9rem", fontWeight: "bold", cursor: issuing ? "not-allowed" : "pointer",
                opacity: issuing ? 0.5 : 1,
              }}
            >
              🔄 Try Printing Again
            </button>
          )}

          <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.72rem", textAlign: "center", margin: "0.7rem 0 0" }}>
            Still stuck? Email the ticket instead (box up top), or text Dan. Printer: {PRINTER_IP}
          </p>
        </div>
      )}

      {/* ── EXPIRY SETTINGS (archived — printed tickets only) ─────────────── */}
      {classic &&
      <div style={{ width: "100%", maxWidth: "320px", marginTop: "0.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <div style={{ flex: 1, height: "1px", background: BRAND.divider }} />
          <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>Ticket Expiry</span>
          <div style={{ flex: 1, height: "1px", background: BRAND.divider }} />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", whiteSpace: "nowrap" }}>Valid for</span>
          <select
            value={expiryMinutes}
            onChange={e => setExpiryMinutes(parseInt(e.target.value))}
            style={{ flex: 1, background: BRAND.card, color: "white", border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", padding: "0.5rem", fontSize: "0.9rem", cursor: "pointer" }}
          >
            {EXPIRY_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Live preview of what prints on the ticket */}
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem", margin: "0.6rem 0 0", textAlign: "center" }}>
          Prints as: <span style={{ color: BRAND.gold }}>{expiryPreview}</span>
        </p>

        {/* Signature line toggle */}
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.6rem", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={showSignature}
            onChange={e => setShowSignature(e.target.checked)}
            style={{ width: "18px", height: "18px", accentColor: BRAND.gold, cursor: "pointer" }}
          />
          <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.8rem" }}>Signature line on ticket</span>
        </label>
      </div>}

      {/* Last batch summary (archived — printed/emailed ticket batches) */}
      {classic && lastBatch && !issuing && (
        <div style={{ background: BRAND.card, borderRadius: "10px", padding: "1rem 1.5rem", width: "100%", maxWidth: "320px", textAlign: "center" }}>
          <p style={{ color: "#4aa4e1", margin: "0 0 0.25rem", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Last batch</p>
          <p style={{ color: "white", margin: "0 0 0.25rem", fontWeight: "bold" }}>
            {[lastFlash > 0 && `${lastFlash}× Flash`, lastCustom > 0 && `${lastCustom}× Custom`, lastMagic > 0 && `${lastMagic}× Magic`].filter(Boolean).join(" + ")}
          </p>
          <p style={{ color: "rgba(255,255,255,0.55)", margin: 0, fontSize: "0.75rem" }}>
            {lastBatch[0].expiryDisplay} · issued {lastBatch[0].expiresAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </p>
          {lastQR && (
            <img src={lastQR} alt="Last QR Code" style={{ width: "120px", height: "120px", marginTop: "0.75rem" }} />
          )}
        </div>
      )}

      {/* Direct queue entry modal — single scrollable pane, picker-first.
          Mirrors the customer /order experience: flash designs surfaced as
          a grid up top, custom-photo branching button below, then shared
          fields (name, colors, notes, comp, print). Subject name + image
          upload only render for custom; freestyle desc only for freestyle.
          No type toggle, no step transitions. */}
      {/* ── ORDER ADDED ────────────────────────────────────────────────────
          The number is the point — it's what Wella reads out loud, so it gets
          the screen. Email and "Next guest" are both ONE tap from here: most
          guests won't leave an address for a simple flash design, and making
          them decline something would be worse than not asking. */}
      {!classic && lastAdded && (
        <div style={{ width: "100%", maxWidth: "460px", display: "flex", flexDirection: "column", gap: "1rem" }}>

          <div style={{ background: BRAND.card, border: `1px solid ${BRAND.gold}`, borderRadius: "16px", padding: "1.4rem 1rem", textAlign: "center" }}>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700 }}>
              In the queue
            </div>
            <div style={{ color: BRAND.gold, fontFamily: FONT.display, fontSize: "4.2rem", fontWeight: 800, lineHeight: 1.05, margin: "0.2rem 0" }}>
              #{lastAdded.queueNumber}
            </div>
            <div style={{ color: "white", fontSize: "1.05rem", fontWeight: 700 }}>{lastAdded.name}</div>
            <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", marginTop: "0.15rem" }}>
              {lastAdded.isFreestyle && lastAdded.freestyle ? `Freestyle — ${lastAdded.freestyle}` : lastAdded.label}
            </div>
          </div>

          {/* Keepsake email — optional, and visibly so. */}
          {emailState === "saved" ? (
            <div style={{ background: "rgba(112,190,68,0.12)", border: "1px solid rgba(112,190,68,0.5)", borderRadius: "12px", padding: "0.8rem 1rem", color: BRAND.green, fontSize: "0.9rem", textAlign: "center" }}>
              ✓ Keepsake will be emailed to {successEmail.trim()}
            </div>
          ) : (
            <div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginBottom: "0.4rem" }}>
                Want their pancake emailed to them? (optional)
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <input
                  type="email"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  value={successEmail}
                  onChange={e => { setSuccessEmail(e.target.value); if (emailState === "error") setEmailState("idle"); }}
                  placeholder="their@email.com"
                  style={{ flex: 1, minWidth: 0, padding: "0.85rem 1rem", background: BRAND.card, border: `1px solid ${BRAND.border}`, borderRadius: "12px", color: "white", fontSize: "1rem" }}
                />
                <button
                  onClick={saveKeepsakeEmail}
                  disabled={!successEmail.trim() || emailState === "saving"}
                  style={{ flexShrink: 0, padding: "0.85rem 1.1rem", background: successEmail.trim() ? BRAND.sky : BRAND.card, color: successEmail.trim() ? BRAND.dark : "rgba(255,255,255,0.35)", border: "none", borderRadius: "12px", fontSize: "0.95rem", fontWeight: 700, cursor: successEmail.trim() ? "pointer" : "not-allowed" }}
                >
                  {emailState === "saving" ? "…" : "Save"}
                </button>
              </div>
              {emailState === "error" && (
                <p style={{ color: BRAND.orange, fontSize: "0.8rem", margin: "0.4rem 0 0" }}>
                  Couldn't save that — the pancake is still in the queue. Try again or add it from the queue later.
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => { setLastAdded(null); setSuccessEmail(""); setEmailState("idle"); }}
            style={{ width: "100%", padding: "1.15rem", background: BRAND.pink, color: "white", border: "none", borderRadius: "14px", fontSize: "1.15rem", fontWeight: 800, cursor: "pointer" }}
          >
            Next guest →
          </button>

          <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem", textAlign: "center" }}>
            No email? They can find this pancake later in the Pancakeopedia.
          </div>
        </div>
      )}

      {/* Order intake. In classic this is the "+ Add Order Directly" modal;
          since 2026-09-25 it IS the register — guests walk up, give a name and
          a design, and that's the whole job. */}
      {(classic ? showDirectEntry : !lastAdded) && (() => {
        const ALL_DESIGNS = allDesigns;
        const isFreestyle = directDesign?.id === "freestyle";
        const canSubmit =
          !!directName.trim() &&
          ((directType === "flash" && directDesign && (!isFreestyle || directFreestyleDesc.trim())) ||
           (directType === "custom"));

        function toggleDirectColor(colorId) {
          setDirectColors(prev =>
            prev.includes(colorId) ? prev.filter(c => c !== colorId)
              : prev.length < 3 ? [...prev, colorId] : prev
          );
        }

        function pickFlashDesign(design) {
          setDirectType("flash");
          setDirectDesign(design);
          // Clear custom-only state in case user was on that path
          setDirectImage(null);
          setDirectCroppedBlob(null);
          directFramingRef.current = {};
          setDirectSubjectName("");
          setDirectError(null);
        }

        function pickCustom() {
          setDirectType("custom");
          setDirectDesign(null);
          setDirectFreestyleDesc("");
          setDirectError(null);
        }

        const selectedFlashId = directType === "flash" ? directDesign?.id : null;

        return (
          <div style={classic
            ? { position: "fixed", inset: 0, background: "rgba(26,10,18,0.9)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 100, padding: "1.5rem", overflowY: "auto" }
            // Inline: no scrim, no z-index, no page-within-a-page. On a phone
            // the pane simply IS the page.
            : { width: "100%", display: "flex", justifyContent: "center" }}>
            <div style={classic
              ? { background: BRAND.panel, borderRadius: "16px", padding: "1.4rem", maxWidth: "420px", width: "100%", border: "1px solid rgba(74,164,225,0.28)", display: "flex", flexDirection: "column", gap: "1rem", marginTop: "1rem", marginBottom: "2rem" }
              : { background: "transparent", padding: 0, maxWidth: "460px", width: "100%", border: "none", display: "flex", flexDirection: "column", gap: "1rem" }}>

              {/* Header — the modal title and its ✕ only make sense as a modal */}
              {classic && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <p style={{ color: "white", fontWeight: "bold", fontSize: "1.05rem", margin: 0 }}>
                  Add Pancake to Queue
                </p>
                <button onClick={() => { setShowDirectEntry(false); resetDirectEntry(); }} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.45)", fontSize: "1.2rem", cursor: "pointer" }}>✕</button>
              </div>
              )}

              {/* SECTION 1 — Flash design grid */}
              <div>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.55rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>⚡ Pick a flash design</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem" }}>
                  {ALL_DESIGNS.map(design => {
                    const selected = selectedFlashId === design.id;
                    return (
                      <button
                        key={design.id}
                        onClick={() => pickFlashDesign(design)}
                        style={{
                          padding: "0.5rem",
                          background: selected ? BRAND.raised : BRAND.card,
                          border: `2px solid ${selected ? BRAND.gold : BRAND.border}`,
                          borderRadius: "10px",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "0.35rem",
                          transition: "transform 0.1s ease",
                          transform: selected ? "scale(1.02)" : "scale(1)",
                        }}
                      >
                        <img
                          src={design.imageUrl}
                          alt={design.label}
                          style={{ width: "100%", aspectRatio: "1", objectFit: "contain", display: "block" }}
                        />
                        <span style={{ color: selected ? BRAND.gold : "rgba(255,255,255,0.75)", fontWeight: 700, fontSize: "0.72rem", textAlign: "center", lineHeight: 1.15 }}>
                          {design.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Freestyle description — only when freestyle is the picked flash design */}
                {selectedFlashId === "freestyle" && (
                  <div style={{ marginTop: "0.7rem" }}>
                    <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.3rem" }}>Freestyle description *</p>
                    <textarea
                      placeholder="What should I draw?"
                      value={directFreestyleDesc}
                      onChange={e => setDirectFreestyleDesc(e.target.value)}
                      rows={2}
                      style={{ width: "100%", padding: "0.65rem 0.85rem", background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", color: "white", fontSize: "0.88rem", resize: "none", fontFamily: FONT.body, boxSizing: "border-box" }}
                    />
                  </div>
                )}
              </div>

              {/* OR divider */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", color: "rgba(255,255,255,0.35)", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                <div style={{ flex: 1, height: 1, background: BRAND.border }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: BRAND.border }} />
              </div>

              {/* SECTION 2 — Custom photo branching */}
              <button
                onClick={pickCustom}
                style={{
                  padding: "0.85rem",
                  background: directType === "custom" ? BRAND.raised : BRAND.card,
                  border: `2px solid ${directType === "custom" ? BRAND.gold : BRAND.border}`,
                  borderRadius: "10px",
                  color: directType === "custom" ? BRAND.gold : "rgba(255,255,255,0.75)",
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                🎨 Custom photo / freestyle
              </button>

              {/* SECTION 3 — Custom-only fields (subject name + image) */}
              {directType === "custom" && (
                <>
                  <div>
                    <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.3rem" }}>Subject name <span style={{ color: "rgba(255,255,255,0.35)" }}>(who/what the pancake depicts)</span></p>
                    <input
                      type="text"
                      placeholder="e.g. Jake the dog"
                      value={directSubjectName}
                      onChange={e => setDirectSubjectName(e.target.value)}
                      style={{ width: "100%", padding: "0.65rem 0.85rem", background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", color: "white", fontSize: "0.88rem", boxSizing: "border-box" }}
                    />
                  </div>

                  <div>
                    <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.3rem" }}>Reference image <span style={{ color: "rgba(255,255,255,0.35)" }}>(optional)</span></p>
                    {!directImage ? (
                      <label style={{ display: "block", padding: "0.6rem 1rem", background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", textAlign: "center", cursor: "pointer" }}>
                        📷 Choose photo
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => { const f = e.target.files?.[0]; if (f) { setDirectImage(f); setDirectCroppedBlob(null); directFramingRef.current = {}; } }}
                          style={{ display: "none" }}
                        />
                      </label>
                    ) : (
                      <div>
                        <FramingTool
                          imageFile={directImage}
                          onCropped={blob => setDirectCroppedBlob(blob)}
                          onStateChange={s => { directFramingRef.current = s; }}
                          size={Math.min(window.innerWidth - 80, 300)}
                        />
                        <button
                          onClick={() => { setDirectImage(null); setDirectCroppedBlob(null); directFramingRef.current = {}; }}
                          style={{ marginTop: "0.5rem", background: "none", border: "1px solid rgba(74,164,225,0.28)", borderRadius: "6px", color: "rgba(255,255,255,0.55)", fontSize: "0.75rem", padding: "0.3rem 0.7rem", cursor: "pointer" }}
                        >
                          ✕ Remove image
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* SHARED — recipient name */}
              <div>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.3rem" }}>Recipient name *</p>
                <input
                  type="text"
                  placeholder="e.g. Edna"
                  value={directName}
                  onChange={e => setDirectName(e.target.value)}
                  style={{ width: "100%", padding: "0.65rem 0.85rem", background: BRAND.card, border: `1px solid ${directName.trim() ? "rgba(74,164,225,0.45)" : BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.88rem", boxSizing: "border-box" }}
                />
              </div>

              {/* SHARED — favorite colors */}
              <div>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.4rem" }}>Favorite colors <span style={{ color: "rgba(255,255,255,0.35)" }}>(up to 3)</span></p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "0.4rem" }}>
                  {COLOR_OPTIONS.map(opt => {
                    const selected = directColors.includes(opt.id);
                    const needsBorder = opt.id === "black" || opt.id === "white";
                    return (
                      <button
                        key={opt.id}
                        onClick={() => toggleDirectColor(opt.id)}
                        title={opt.label}
                        style={{
                          width: "100%", aspectRatio: "1", borderRadius: "50%",
                          background: opt.hex,
                          border: selected ? `3px solid white` : needsBorder ? `2px solid rgba(255,255,255,0.35)` : "3px solid transparent",
                          cursor: (!selected && directColors.length >= 3) ? "not-allowed" : "pointer",
                          opacity: (!selected && directColors.length >= 3) ? 0.4 : 1,
                          boxSizing: "border-box",
                          transform: selected ? "scale(1.15)" : "scale(1)",
                          transition: "transform 0.15s ease",
                        }}
                      />
                    );
                  })}
                </div>
                {directColors.length > 0 && (
                  <p style={{ color: BRAND.gold, fontSize: "0.72rem", margin: "0.4rem 0 0" }}>
                    {directColors.map(id => COLOR_OPTIONS.find(c => c.id === id)?.label).join(", ")}
                  </p>
                )}
              </div>

              {/* SHARED — order notes */}
              <div>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.7rem", margin: "0 0 0.3rem" }}>Order notes <span style={{ color: "rgba(255,255,255,0.35)" }}>(optional)</span></p>
                <textarea
                  placeholder="Anything special to know?"
                  value={directNotes}
                  onChange={e => setDirectNotes(e.target.value)}
                  rows={2}
                  style={{ width: "100%", padding: "0.65rem 0.85rem", background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", color: "white", fontSize: "0.88rem", resize: "none", fontFamily: FONT.body, boxSizing: "border-box" }}
                />
              </div>

              {/* SHARED — comp toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <button
                  onClick={() => setDirectIsComp(v => !v)}
                  style={{ padding: "0.5rem 1rem", border: `1px solid ${directIsComp ? BRAND.orange : BRAND.border}`, borderRadius: "8px", background: directIsComp ? "#3a2200" : "transparent", color: directIsComp ? BRAND.orange : "rgba(255,255,255,0.55)", fontSize: "0.82rem", fontWeight: "bold", cursor: "pointer", flexShrink: 0 }}
                >
                  {directIsComp ? "✓ Comped" : "Paid"}
                </button>
                {directIsComp && (
                  <input
                    type="text"
                    placeholder="Comp reason"
                    value={directCompReason}
                    onChange={e => setDirectCompReason(e.target.value)}
                    style={{ flex: 1, padding: "0.45rem 0.7rem", background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", color: "white", fontSize: "0.82rem" }}
                  />
                )}
              </div>

              {/* SHARED — print toggle (archived: thermal era) */}
              {classic && <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.55rem 0.7rem", background: BRAND.card, border: "1px solid rgba(74,164,225,0.28)", borderRadius: "8px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={directPrintRequested}
                  onChange={e => setDirectPrintRequested(e.target.checked)}
                  style={{ width: "16px", height: "16px", accentColor: BRAND.gold }}
                />
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.85rem" }}>🖨 Print ticket now</span>
              </label>}

              {directError && <p style={{ color: BRAND.orange, fontSize: "0.85rem", margin: 0 }}>{directError}</p>}

              {/* Submit */}
              <button
                onClick={handleDirectEntry}
                disabled={!canSubmit || directSubmitting}
                style={{
                  padding: "0.9rem",
                  background: (canSubmit && !directSubmitting) ? BRAND.pink : BRAND.card,
                  color: (canSubmit && !directSubmitting) ? "white" : "rgba(255,255,255,0.35)",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  cursor: (canSubmit && !directSubmitting) ? "pointer" : "not-allowed",
                }}
              >
                {directSubmitting
                  ? "Adding…"
                  : !directType
                    ? "Pick a design above"
                    : !directName.trim()
                      ? "Enter recipient name"
                      : (wantsPrint ? "🖨 Add & Print" : "Add to Queue")}
              </button>

            </div>
          </div>
        );
      })()}


      {/* 📱 Screen-QR modal — guest-facing: big QR, minimal words. */}
      {showScreenQR && lastBatch && lastBatch.length > 0 && (() => {
        const idx = Math.min(screenQRIndex, lastBatch.length - 1);
        const job = lastBatch[idx];
        const typeMeta = {
          flash:  { emoji: "⚡", label: "Flash Pancake" },
          custom: { emoji: "📷", label: "Custom Photo Pancake" },
          magic:  { emoji: "✨", label: "Magic Ticket" },
        }[job.ticketType] || { emoji: "🎟", label: "Golden Ticket" };
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(4,10,26,0.96)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 120, padding: "1.5rem", gap: "1rem" }}>
            <div style={{ color: BRAND.gold, fontSize: "1.35rem", fontWeight: "bold", textAlign: "center" }}>
              {typeMeta.emoji} {typeMeta.label}
            </div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "1rem", textAlign: "center" }}>
              Scan with your camera to set up your pancake
            </div>
            <div style={{ background: "white", borderRadius: "18px", padding: "1rem" }}>
              <img src={job.qrDataUrl} alt="Ticket QR" style={{ width: "min(72vw, 380px)", height: "min(72vw, 380px)", display: "block" }} />
            </div>
            {lastBatch.length > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                <button onClick={() => setScreenQRIndex((i) => Math.max(0, i - 1))} disabled={idx === 0}
                  style={{ background: BRAND.card, border: "none", borderRadius: "10px", color: "white", fontSize: "1.3rem", padding: "0.4rem 1rem", cursor: idx === 0 ? "not-allowed" : "pointer", opacity: idx === 0 ? 0.4 : 1 }}>‹</button>
                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}>Ticket {idx + 1} of {lastBatch.length}</span>
                <button onClick={() => setScreenQRIndex((i) => Math.min(lastBatch.length - 1, i + 1))} disabled={idx === lastBatch.length - 1}
                  style={{ background: BRAND.card, border: "none", borderRadius: "10px", color: "white", fontSize: "1.3rem", padding: "0.4rem 1rem", cursor: idx === lastBatch.length - 1 ? "not-allowed" : "pointer", opacity: idx === lastBatch.length - 1 ? 0.4 : 1 }}>›</button>
              </div>
            )}
            <button onClick={() => setShowScreenQR(false)}
              style={{ marginTop: "0.5rem", background: BRAND.card, border: "1px solid rgba(255,255,255,0.2)", borderRadius: "10px", color: "rgba(255,255,255,0.75)", fontSize: "0.95rem", fontWeight: "bold", padding: "0.7rem 1.6rem", cursor: "pointer" }}>
              Done — back to register
            </button>
          </div>
        );
      })()}

      {/* Queue-closed print confirmation */}
      {showQueueClosedWarning && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.82)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "1.5rem" }}>
          <div style={{ background: BRAND.panel, borderRadius: "16px", padding: "1.75rem 1.5rem", maxWidth: "320px", width: "100%", textAlign: "center", border: "1px solid rgba(74,164,225,0.28)" }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.6rem" }}>🚫</div>
            <p style={{ color: "#e8b44b", fontWeight: "bold", fontSize: "1.1rem", margin: "0 0 0.5rem" }}>Queue is closed</p>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem", margin: "0 0 1.5rem", lineHeight: 1.5 }}>
              No more orders are being taken this shift. Print anyway?
            </p>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                onClick={() => setShowQueueClosedWarning(false)}
                style={{ flex: 1, padding: "0.9rem", background: BRAND.card, color: "rgba(255,255,255,0.55)", border: "none", borderRadius: "10px", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowQueueClosedWarning(false); issueBatch(); }}
                style={{ flex: 1, padding: "0.9rem", background: BRAND.gold, color: BRAND.dark, border: "none", borderRadius: "10px", fontSize: "1rem", fontWeight: "bold", cursor: "pointer" }}
              >
                Print Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
}

export default RegisterView;
